import {
  Box,
  Coin,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  FormattedDateTime,
  Grid,
  Heading,
  Icon,
  InlineStack,
  PageHeaderDescription,
  TabNav,
  TabNavList,
  TabNavTab,
  Text,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import { objectIcon } from '../components/object-type-header';
import { objectName } from '@/services/db/object/entity';
import { CREATED_IN_CIO_AT } from '@/utils/attributes';
import { isEmptyString } from '@/utils/string';
import { useObjectDetail } from './context';

const DISABLED_OPTION_TOOLTIP = 'Contact an Admin to access this feature.';

type ObjectDetailHeaderProps = {
  onDelete: () => void;
  onExport: () => void;
  onAddRelationships: () => void;
};

function objectCreatedAtKey(attributes: Record<string, string>): string {
  return !isEmptyString(attributes.created_at) ? 'created_at' : CREATED_IN_CIO_AT;
}

export function ObjectDetailHeader(props: ObjectDetailHeaderProps) {
  const detail = useObjectDetail();
  const { record, objectType, hrefs } = detail;

  const createdAtKey = objectCreatedAtKey(record.attributes);
  const createdAtValue = record.attributes[createdAtKey];
  const shouldShowCreatedAt = !isEmptyString(createdAtValue);
  const isCreatedAtRedacted = detail.shouldRedactCreatedAt || createdAtValue === '******';
  const createdAtSeconds = Number(createdAtValue);

  const options = [
    {
      label: 'Delete',
      onClick: props.onDelete,
      isDisabled: !detail.canDelete,
    },
    {
      label: 'Export profile data',
      onClick: props.onExport,
      isDisabled: !detail.canExport,
    },
    {
      label: 'Add Relationships',
      onClick: props.onAddRelationships,
      isDisabled: !detail.canCreateRelationships,
    },
  ];

  return (
    <Box>
      <Grid columns="1fr 2fr">
        <Box>
          <InlineStack alignBlock="center" alignInline="space-between">
            <Box mr="200">
              <Coin size="xl" backgroundColor="invert-bold" color="invert">
                <Icon name={objectIcon(objectType.icon)} />
              </Coin>
            </Box>
            <Box flex={1} maxW="full" minW="0" overflowWrap="words">
              <Heading level="2" className="fs-mask" data-test-object-name>
                {objectName(record)}
              </Heading>
              {shouldShowCreatedAt ? (
                <PageHeaderDescription data-test-object-created-at display="flex" alignItems="center" gap="050">
                  <Text color="inherit">
                    Created at{' '}
                    {isCreatedAtRedacted ? (
                      '******'
                    ) : (
                      <FormattedDateTime date={Number.isNaN(createdAtSeconds) ? null : createdAtSeconds} />
                    )}
                  </Text>
                  {!isCreatedAtRedacted && createdAtKey === CREATED_IN_CIO_AT ? (
                    <Tooltip placement="top" isCustom>
                      {(context) => (
                        <>
                          <Icon
                            name="help"
                            size="sm"
                            color="subtle"
                            aria-label="Show information about the created at value"
                            ref={context.reference.setReference}
                            {...context.reference.getReferenceProps()}
                          />
                          <TooltipContent>
                            Referring to {CREATED_IN_CIO_AT}. This value is auto-generated by Customer.io
                          </TooltipContent>
                        </>
                      )}
                    </Tooltip>
                  ) : null}
                </PageHeaderDescription>
              ) : null}
            </Box>
          </InlineStack>
        </Box>
        <Box>
          <Box display="flex" justifyContent="flex-end" gap="150">
            <DropdownMenu>
              <DropdownMenuTriggerButton
                data-test-object-action-dropdown
                variant="secondary"
                icon="chevron-down"
                iconPosition="trailing"
                // Ember disables the whole menu without any object create/edit/
                // delete permission, even for viewers who could export or add
                // relationships — kept for parity.
                isDisabled={!detail.canConfigureObjects}
              >
                Options
              </DropdownMenuTriggerButton>
              <DropdownMenuContent>
                {options.map((option) => (
                  <DropdownMenuItem
                    key={option.label}
                    onClick={option.onClick}
                    isDisabled={option.isDisabled}
                    tooltip={option.isDisabled ? DISABLED_OPTION_TOOLTIP : undefined}
                  >
                    {option.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </Box>
        </Box>
      </Grid>

      <TabNav my="250" aria-label={`${objectName(record) ?? 'Object'} pages`}>
        <TabNavList>
          <TabNavTab href={hrefs.overview} isActive data-test-object-nav-link="overview">
            Overview
          </TabNavTab>
          <TabNavTab href={hrefs.tabs.attributes} isActive={false} data-test-object-nav-link="attributes">
            Attributes
          </TabNavTab>
          <TabNavTab href={hrefs.tabs.relationships} isActive={false} data-test-object-nav-link="relationships">
            Relationships
          </TabNavTab>
          <TabNavTab href={hrefs.tabs.activity} isActive={false} data-test-object-nav-link="activity">
            Activity
          </TabNavTab>
        </TabNavList>
      </TabNav>
    </Box>
  );
}
